import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdShoppingCart,
  MdReceiptLong,
  MdInventory,
  MdLocalShipping,
  MdEmojiEvents,
  MdTrendingUp,
  MdEventBusy,
  MdHourglassTop,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/supplier-purchase';
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });
const label = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');
const dash = <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;
const RECV_STATUS = { pending: ['#fef3c7', '#92400e'], partially_assigned: ['#e0f2fe', '#075985'], completed: ['#dcfce7', '#166534'] };
const PAY_STATUS = {
  paid: ['#dcfce7', '#166534', 'Paid'],
  partial: ['#fef3c7', '#92400e', 'Part paid'],
  unpaid: ['#fee2e2', '#991b1b', 'Unpaid'],
  awaiting_putaway: ['#e0f2fe', '#075985', 'Awaiting put-away'],
  not_in_ledger: ['#f1f5f9', '#475569', 'Not in ledger'],
};
const chip = (map, v) => {
  const [bg, fg, text] = map[v] || ['#f1f5f9', '#475569'];
  return <Chip size="small" label={text || label(v)} sx={pill(bg, fg)} />;
};

const TwoLine = ({ top, bottom }) => (
  <Box sx={{ lineHeight: 1.25, minWidth: 150 }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>{top}</Typography>
    {bottom && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{bottom}</Typography>}
  </Box>
);

const col = (key, lbl, opts = {}) => ({ key, label: lbl, ...opts });
const money = (key, lbl, extra = {}) => col(key, lbl, { numeric: true, text: (r) => r[key] ?? '', cell: (r) => (r[key] === null || r[key] === undefined ? dash : inr(r[key])), print: (r) => (r[key] === null || r[key] === undefined ? '-' : inr(r[key])), ...extra });
const supplierCol = col('supplier', 'Supplier', { text: (r) => r.supplier, cell: (r) => <TwoLine top={r.supplier} bottom={[r.supplierCode, r.gstin && `GSTIN ${r.gstin}`].filter(Boolean).join(' · ')} />, extra: (r) => ({ 'Supplier Code': r.supplierCode, GSTIN: r.gstin }) });

const TABS = [
  {
    key: 'suppliers',
    label: 'By Supplier',
    defaultSort: 'value_desc',
    sorts: [['value_desc', 'Purchase value: high to low'], ['invoices_desc', 'Most invoices'], ['outstanding_desc', 'Outstanding: high to low'], ['recent', 'Most recent purchase'], ['name_asc', 'Name: A to Z']],
    columns: [
      col('rank', '#', { center: true, text: (r, i) => i, skipExport: true }),
      supplierCol,
      col('invoices', 'Invoices', { numeric: true, text: (r) => r.invoices }),
      col('products', 'Products', { numeric: true, text: (r) => r.products }),
      col('quantity', 'Units', { numeric: true, text: (r) => r.quantity }),
      money('value', 'Purchase Value', { bold: true }),
      col('sharePct', '% of Purchases', {
        text: (r) => `${r.sharePct}%`,
        cell: (r) => (
          <Box sx={{ minWidth: 110, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress variant="determinate" value={Math.min(r.sharePct, 100)} sx={{ flex: 1, height: 8, borderRadius: 4, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#0ea5e9,#2563eb)', borderRadius: 4 } }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: FONT, minWidth: 42, textAlign: 'right' }}>{r.sharePct}%</Typography>
          </Box>
        ),
      }),
      money('avgInvoice', 'Avg Invoice'),
      money('outstanding', 'Outstanding', { cell: (r) => <span style={{ color: r.outstanding > 0 ? '#b45309' : undefined, fontWeight: 700 }}>{inr(r.outstanding)}</span> }),
      col('lastPurchase', 'Last Purchase', { text: (r) => fmtDate(r.lastPurchase) }),
      col('status', 'Status', { text: (r) => label(r.status), cell: (r) => <Chip size="small" label={label(r.status)} sx={r.status === 'active' ? pill('#dcfce7', '#166534') : pill('#f1f5f9', '#64748b')} /> }),
    ],
  },
  {
    key: 'invoices',
    label: 'Purchase Invoices',
    defaultSort: 'date_desc',
    sorts: [['date_desc', 'Newest first'], ['date_asc', 'Oldest first'], ['value_desc', 'Value: high to low'], ['outstanding_desc', 'Outstanding: high to low']],
    columns: [
      col('invoiceNumber', 'Invoice No.', { bold: true, text: (r) => r.invoiceNumber }),
      col('invoiceDate', 'Invoice Date', { text: (r) => fmtDate(r.invoiceDate) }),
      col('receiptId', 'Receipt ID', { text: (r) => r.receiptId }),
      supplierCol,
      col('warehouse', 'Warehouse', { text: (r) => r.warehouse }),
      col('lines', 'Lines', { numeric: true, text: (r) => r.lines }),
      col('quantity', 'Units', { numeric: true, text: (r) => r.quantity }),
      money('value', 'Invoice Value', { bold: true }),
      money('paid', 'Paid'),
      money('outstanding', 'Outstanding', { cell: (r) => (r.outstanding === null ? dash : <span style={{ color: r.outstanding > 0 ? '#b45309' : undefined, fontWeight: 700 }}>{inr(r.outstanding)}</span>) }),
      col('paymentStatus', 'Payment', { text: (r) => PAY_STATUS[r.paymentStatus]?.[2] || r.paymentStatus, cell: (r) => chip(PAY_STATUS, r.paymentStatus) }),
      col('status', 'Receiving', { text: (r) => label(r.status), cell: (r) => chip(RECV_STATUS, r.status) }),
    ],
  },
  {
    key: 'products',
    label: 'By Product',
    defaultSort: 'value_desc',
    sorts: [['value_desc', 'Purchase value: high to low'], ['qty_desc', 'Units: high to low'], ['pricechange_desc', 'Biggest price increase'], ['margin_asc', 'Lowest margin first']],
    columns: [
      col('product', 'Product', { text: (r) => r.product, cell: (r) => <TwoLine top={r.product} bottom={[r.brand, r.productCode].filter(Boolean).join(' · ')} />, extra: (r) => ({ Code: r.productCode, Brand: r.brand }) }),
      col('quantity', 'Units Bought', { numeric: true, bold: true, text: (r) => r.quantity }),
      money('value', 'Purchase Value', { bold: true }),
      money('avgPrice', 'Avg Price'),
      col('range', 'Price Range', { text: (r) => (r.minPrice === null ? '' : `${r.minPrice} – ${r.maxPrice}`), cell: (r) => (r.minPrice === null ? dash : `${inr(r.minPrice)} – ${inr(r.maxPrice)}`) }),
      money('lastPrice', 'Last Price'),
      col('priceChangePct', 'Price Change', {
        center: true,
        text: (r) => (r.priceChangePct === null ? '' : `${r.priceChangePct}%`),
        cell: (r) => {
          if (r.priceChangePct === null) return dash;
          const up = r.priceChangePct > 0;
          if (r.priceChangePct === 0) return <Chip size="small" label="0%" sx={pill('#f1f5f9', '#475569')} />;
          return <Chip size="small" label={`${up ? '▲' : '▼'} ${Math.abs(r.priceChangePct)}%`} sx={up ? pill('#fee2e2', '#b91c1c') : pill('#dcfce7', '#166534')} />;
        },
      }),
      money('sellingPrice', 'Selling Price'),
      col('marginPct', 'Margin', {
        center: true,
        text: (r) => (r.marginPct === null ? '' : `${r.marginPct}%`),
        cell: (r) => (r.marginPct === null ? dash : <Chip size="small" label={`${r.marginPct}%`} sx={r.marginPct >= 30 ? pill('#dcfce7', '#166534') : r.marginPct >= 15 ? pill('#fef3c7', '#92400e') : pill('#fee2e2', '#b91c1c')} />),
      }),
      col('suppliers', 'Suppliers', {
        text: (r) => r.suppliers,
        cell: (r) => (
          <Tooltip title={r.suppliers} arrow>
            <Typography sx={{ fontSize: 11.5, fontFamily: FONT, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.supplierCount > 1 ? `${r.supplierCount} suppliers` : r.suppliers}</Typography>
          </Tooltip>
        ),
      }),
    ],
  },
  {
    key: 'expiry',
    label: 'Expiry Watch',
    defaultSort: 'expiry_asc',
    sorts: [['expiry_asc', 'Expiring soonest'], ['value_desc', 'Value: high to low']],
    columns: [
      col('product', 'Product', { text: (r) => r.product, cell: (r) => <TwoLine top={r.product} bottom={r.productCode} />, extra: (r) => ({ Code: r.productCode }) }),
      col('batchNo', 'Batch', { bold: true, text: (r) => r.batchNo }),
      col('supplier', 'Supplier', { text: (r) => r.supplier }),
      col('invoiceNumber', 'Invoice No.', { text: (r) => r.invoiceNumber }),
      col('quantity', 'Units Received', { numeric: true, text: (r) => r.quantity }),
      money('value', 'Batch Value'),
      col('manufactureDate', 'Mfg Date', { text: (r) => (r.manufactureDate ? fmtDate(r.manufactureDate) : '') }),
      col('expireDate', 'Expiry Date', { text: (r) => fmtDate(r.expireDate) }),
      col('daysToExpiry', 'Expires In', {
        center: true,
        text: (r) => (r.daysToExpiry < 0 ? `Expired ${-r.daysToExpiry}d ago` : `${r.daysToExpiry} days`),
        cell: (r) =>
          r.daysToExpiry < 0 ? (
            <Chip size="small" label={`Expired ${-r.daysToExpiry}d ago`} sx={pill('#1f2937', '#fff')} />
          ) : (
            <Chip size="small" label={`${r.daysToExpiry} days`} sx={r.daysToExpiry <= 30 ? pill('#fee2e2', '#b91c1c') : r.daysToExpiry <= 60 ? pill('#ffedd5', '#9a3412') : pill('#fef3c7', '#92400e')} />
          ),
      }),
    ],
  },
];

const SupplierPurchaseReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [supplier, setSupplier] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [status, setStatus] = useState('');
  const [payment, setPayment] = useState('');
  const [expiryDays, setExpiryDays] = useState('90');
  const [sortBy, setSortBy] = useState(TABS[0].defaultSort);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { tab: tab.key, sortBy, expiryDays };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (supplier) p.supplier = supplier;
    if (warehouse) p.warehouse = warehouse;
    if (status) p.status = status;
    if (payment) p.payment = payment;
    return p;
  }, [tab.key, sortBy, expiryDays, from, to, search, supplier, warehouse, status, payment]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (i, after) => {
    setTabIndex(i);
    setSortBy(TABS[i].defaultSort);
    setPage(0);
    if (after) after();
  };
  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };
  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };
  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };
  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setSupplier('');
    setWarehouse('');
    setStatus('');
    setPayment('');
    setExpiryDays('90');
    setSortBy(tab.defaultSort);
    setPage(0);
  };

  const exportColumns = tab.columns.filter((c) => !c.skipExport);
  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of exportColumns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `Supplier_Purchase_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Supplier Purchase — ${tab.label}`,
          subtitle: `${rangeLabel(from, to)} · ${rows.length} rows`,
          headers: exportColumns.map((c) => c.label),
          cells: rows.map((r, i) => exportColumns.map((c) => ({ value: c.print ? c.print(r) : c.text(r, i + 1), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const opts = data?.filterOptions || { suppliers: [], warehouses: [] };
  const baseIndex = page * rowsPerPage;

  const cards = [
    { label: 'Purchase Value', value: inr(stats.purchaseValue), icon: MdShoppingCart, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'Before GST' },
    { label: 'Invoices', value: (stats.invoices || 0).toLocaleString('en-IN'), icon: MdReceiptLong, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `Avg ${inr(stats.avgInvoice)}`, onClick: () => goToTab(1), active: tab.key === 'invoices' && !payment },
    { label: 'Units Purchased', value: (stats.units || 0).toLocaleString('en-IN'), icon: MdInventory, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: `${stats.products || 0} products · ${stats.suppliers || 0} suppliers` },
    { label: 'Top Supplier', value: stats.topSupplier?.name || '—', icon: MdEmojiEvents, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: stats.topSupplier ? `${stats.topSupplier.sharePct}% of purchases` : 'No purchases' },
    { label: 'Unpaid on These Invoices', value: inr(stats.outstanding), icon: MdLocalShipping, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'Click for unpaid / part paid', onClick: () => goToTab(1, () => setPayment('partial')), active: tab.key === 'invoices' && payment === 'partial' },
    { label: 'Price Increases', value: (stats.priceIncreases || 0).toLocaleString('en-IN'), icon: MdTrendingUp, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: 'Products bought at a higher price', onClick: () => goToTab(2, () => setSortBy('pricechange_desc')), active: tab.key === 'products' && sortBy === 'pricechange_desc' },
    { label: 'Near-Expiry Stock', value: inr(stats.nearExpiryValue), icon: MdEventBusy, gradient: 'linear-gradient(135deg,#be123c,#881337)', sub: `Within ${stats.expiryDays || 90} days · ${inr(stats.expiredValue)} expired`, onClick: () => goToTab(3), active: tab.key === 'expiry' },
    { label: 'Awaiting Put-away', value: (stats.pendingPutaway || 0).toLocaleString('en-IN'), icon: MdHourglassTop, gradient: 'linear-gradient(135deg,#64748b,#334155)', sub: 'Receivings not completed', onClick: () => goToTab(1, () => setPayment('awaiting_putaway')), active: tab.key === 'invoices' && payment === 'awaiting_putaway' },
  ];

  const activeFilterCount = [from || to, search, supplier, warehouse, status, payment, sortBy !== tab.defaultSort].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Supplier Purchase Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>What you bought, from whom, at what price — and what's close to expiry</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From (invoice date)" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label="Search invoice no. / receipt / supplier"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Supplier" size="small" fullWidth value={supplier} onChange={setFilter(setSupplier)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All suppliers</MenuItem>
              {opts.suppliers.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Warehouse" size="small" fullWidth value={warehouse} onChange={setFilter(setWarehouse)}>
              <MenuItem value="">All warehouses</MenuItem>
              {opts.warehouses.map((w) => (
                <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Receiving status" size="small" fullWidth value={status} onChange={setFilter(setStatus)}>
              <MenuItem value="">All</MenuItem>
              {Object.keys(RECV_STATUS).map((s) => (
                <MenuItem key={s} value={s}>{label(s)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Payment status" size="small" fullWidth value={payment} onChange={setFilter(setPayment)}>
              <MenuItem value="">All</MenuItem>
              {Object.entries(PAY_STATUS).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v[2]}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {tab.key === 'expiry' && (
            <Grid item xs={12} sm={6} md={3} lg={2.5}>
              <TextField select label="Expiring within" size="small" fullWidth value={expiryDays} onChange={setFilter(setExpiryDays)}>
                {['30', '60', '90', '180', '365'].map((d) => (
                  <MenuItem key={d} value={d}>{d} days</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {tab.sorts.map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} rows</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>Nothing matches the selected filters.</TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { background: '#f8fafc' }, boxShadow: tab.key === 'expiry' && r.daysToExpiry <= 30 ? 'inset 4px 0 0 #dc2626' : 'none' }}>
                  {tab.columns.map((c) => (
                    <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: c.bold ? 700 : 500, color: '#334155' }}>
                      {c.cell ? c.cell(r, baseIndex + idx + 1) : c.text(r, baseIndex + idx + 1)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierPurchaseReport;
